import React, { forwardRef } from 'react';
import type { ClickEvent, ChangeEvent, Event, DataLayerObject, Tracking } from './types';

const trackedLabel: string[] = [];

export const mapSectionKey: Record<
  keyof Omit<PvWizardResultsQueryVariables, 'roofDetails'>,
  { label: string; funnelStep: number }
> = {
  houseType: { label: 'Haustyp', funnelStep: 2 },
  householdSize: { label: 'Personenanzahl und Stromverbrauch', funnelStep: 3 },
  roofPitch: { label: 'Dachneigung', funnelStep: 4 },
  roofAlignment: { label: 'Ausrichtung Solaranlage', funnelStep: 5 },
  hasVehicle: { label: 'Solaranlage für E-Auto', funnelStep: 6 },
  vehicleType: { label: 'E-Auto Typ', funnelStep: 7 },
  avgMileage: { label: 'E-Auto Laufleistung im Jahr', funnelStep: 8 },
  daysToCharge: { label: 'E-Auto Ladestrategie', funnelStep: 9 },
  wantsSolarStorage: { label: 'Solarspeicher', funnelStep: 10 }
};

export const trackEvent = (data: Event) => {
  if (typeof window !== 'undefined' && !window.dataLayer) {
    // Create new dataLayer, unless it is present.
    // Needed for local development
    window.dataLayer = [];
  }

  if (window.dataLayer) {
    if (data.element && !(data.trackOnce && trackedLabel.includes(data.label))) {
      if (data.trackOnce && trackedLabel.includes(data.type)) {
        return;
      }

      const event: DataLayerObject = {
        event: data.type,
        ...(data.type === 'product_order'
          ? {
              product: [
                {
                  productName: 'ADAC Solarrechner',
                  productID: '',
                  productStatus: 'lead',
                  productPrice: '0.00',
                  productVariant: data?.productVariant,
                  productBrand: 'ADAC SE',
                  productCategory: 'Solar',
                  productType: 'Solarrechner',
                  contractType: 'single',
                  productQty: 1
                }
              ],
              order: {
                orderId: crypto.randomUUID(),
                orderStatus: 'forwarded',
                orderTotalPrice: '0.00'
              }
            }
          : {
              eventData: {
                eventAction: `${String(data.element).trim()} | ${String(data.interaction).trim()}`,
                eventCategory: `Funnel`,
                eventLabel: String(data.label).trim(),
                eventValue: data.value,
                product: 'ADAC Solarrechner',
                funnelStep: data.funnelStep,
                funnelStepInhalt: data.funnelStepInhalt,
                productVariant: data.productVariant
              }
            })
      };
      window.dataLayer.push(event);
      trackedLabel.push(data.label);
    }
  }
};

export const trackClick = (data: ClickEvent) => {
  trackEvent({
    type: 'click',
    interaction: data.interaction || 'funnel_click',
    ...data
  });
};

export const trackChange = (data: ChangeEvent) => {
  trackEvent({
    type: 'click',
    interaction: data.interaction || 'change',
    ...data
  });
};

export const trackProductOrder = (data: ClickEvent) => {
  trackEvent({ type: 'product_order', interaction: 'click', ...data });
};

export type InjectedTrackingProps = {
  onClick?: React.ReactEventHandler;
  onChange?: React.ReactEventHandler;
  'data-tracking'?: string;
};

type TrackingProps = {
  tracking?: Tracking;
};

export const withTracking = <P extends InjectedTrackingProps>(
  WrappedComponent: React.ComponentType<P>
) => {
  const WithTracking = forwardRef<Element, P & TrackingProps>(
    ({ tracking = {}, onClick, onChange, ...props }, ref) => {
      const clickHandler: React.MouseEventHandler = e => {
        onClick?.(e);

        if (tracking.click) {
          trackClick(tracking.click);
        }
      };

      const isSelectElement = (
        element: HTMLSelectElement | HTMLInputElement | HTMLTextAreaElement
      ): element is HTMLSelectElement => 'options' in element;

      const changeHandler: React.ChangeEventHandler<
        HTMLSelectElement | HTMLInputElement | HTMLTextAreaElement
      > = e => {
        if (onChange) {
          onChange(e);
        }

        if (tracking.change) {
          if (isSelectElement(e.currentTarget)) {
            trackChange({
              ...tracking.change,
              label: e.currentTarget.options[e.currentTarget.selectedIndex].text
            });
          } else {
            trackChange({ ...tracking.change, label: e.currentTarget.value });
          }
        }
      };

      return (
        <WrappedComponent
          onClick={clickHandler}
          onChange={changeHandler}
          data-tracking={JSON.stringify(tracking)}
          {...(props as P)}
          ref={ref}
        />
      );
    }
  );

  return WithTracking;
};
